import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Receipt,
  Wallet,
  TrendingDown,
  TrendingUp,
  Users,
  ShieldCheck,
  X,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { api, BRL } from "@/lib/api";

const MinusRow = ({ icon: Icon, label, value, hint, danger }) => (
  <div className="flex items-start gap-2 py-1.5">
    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${danger ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[12px] font-semibold text-slate-800 leading-tight">{label}</p>
      {hint && <p className="text-[10px] text-slate-500 leading-tight">{hint}</p>}
    </div>
    <p className={`text-[13px] font-black tabular-nums ${danger ? "text-rose-700" : "text-slate-700"}`}>
      {danger ? "− " : ""}{BRL(value || 0)}
    </p>
  </div>
);

const EXP_LABEL = {
  material_escola: "Materiais",
  funcionarios: "Funcionários",
  gasolina: "Gasolina e transporte",
  locacao: "Locações e equipamentos",
  servicos_terceirizados: "Serviços terceirizados",
  ferramentas: "Ferramentas",
  alimentacao: "Alimentação",
  outros: "Outras despesas",
};

const AdjustmentsBox = ({ data }) => {
  const socios = data.socio_adjustments || [];
  const caixa = Number(data.caixa_adjustment || 0);
  if (!socios.length && Math.abs(caixa) < 0.01) return null;
  return (
    <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="finance-result-adjustments">
      <p className="text-[10px] uppercase tracking-widest text-amber-800 font-black mb-1">Correção controlada</p>
      <p className="text-[11px] text-amber-900 leading-snug mb-2">
        O orçamento foi recalculado depois de valores já repassados. Nada foi apagado — a diferença fica como ajuste.
      </p>
      {Math.abs(caixa) >= 0.01 && (
        <p className="text-[11.5px] text-amber-900">
          Caixa: repassado {BRL(data.caixa_ack_value)} → agora {BRL(data.reserve_value)} (<b>{caixa > 0 ? "+" : "−"}{BRL(Math.abs(caixa))}</b> já refletido no saldo)
        </p>
      )}
      {socios.map((a) => (
        <p key={a.socio_id} className="text-[11.5px] text-amber-900" data-testid={`finance-adj-${a.socio_id}`}>
          {a.socio_name}: recebeu {BRL(a.paid)}, devido {BRL(a.due)} → <b>desconto de {BRL(Math.abs(a.diff))}</b> no próximo pagamento
        </p>
      ))}
    </div>
  );
};

/**
 * FinanceResultDialog
 * -------------------
 * Shows the automatic finance breakdown for a budget after NF data is saved.
 * Expected props:
 *  - open: boolean
 *  - onOpenChange: (v: boolean) => void
 *  - budgetId: string
 */
export default function FinanceResultDialog({ open, onOpenChange, budgetId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [eligible, setEligible] = useState(true);
  const [reason, setReason] = useState("");
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    if (!open || !budgetId) return;
    let abort = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/finance/budgets/${budgetId}/result`);
        if (abort) return;
        const snap = r.data?.snapshot || r.data?.preview || null;
        setIsPreview(!r.data?.snapshot && !!r.data?.preview);
        setEligible(!!snap);
        setReason(snap ? "" : "Orçamento não elegível para apuração.");
        setData(snap);
      } catch (e) {
        if (abort) return;
        setEligible(false);
        setReason(e?.response?.data?.detail || "Falha ao calcular o resultado");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [open, budgetId]);

  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[94vw] max-w-md p-0 overflow-hidden gap-0 max-h-[90vh] grid grid-rows-[auto_1fr] [&>button]:hidden"
        data-testid="finance-result-dialog"
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 px-4 pt-4 pb-4 text-white">
          <button
            type="button"
            onClick={close}
            aria-label="Fechar"
            data-testid="finance-result-close"
            className="absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors z-10"
          >
            <X className="w-5 h-5 text-white" strokeWidth={2.6} />
          </button>
          <div className="flex items-center gap-2 mb-2 pr-10">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
              <Wallet className="w-5 h-5 text-emerald-400" strokeWidth={2.2} />
            </div>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-[9px] font-bold uppercase tracking-widest">
              Apuração
            </div>
          </div>
          <DialogTitle className="text-lg font-black text-white leading-tight">
            Demonstrativo financeiro
          </DialogTitle>
          <DialogDescription className="text-[12px] text-white/70 mt-1 leading-snug">
            Receita, despesas, impostos, caixa e divisão entre os sócios deste orçamento.
          </DialogDescription>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-4 py-4 bg-white">
          {loading ? (
            <div className="py-10 flex flex-col items-center text-slate-500 gap-2">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <p className="text-[12px]">Calculando resultado...</p>
            </div>
          ) : !eligible || !data ? (
            <div className="py-6 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="finance-result-not-eligible">
              <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-[13px] font-black text-amber-900">Sem apuração disponível</p>
                <p className="text-[11.5px] text-amber-800 leading-snug mt-0.5">{reason}</p>
                <p className="text-[10px] text-amber-700 mt-1">
                  O novo módulo financeiro contabiliza somente notas fiscais emitidas a partir de <strong>out/2026</strong>.
                </p>
              </div>
            </div>
          ) : (
            <>
              {isPreview && (
                <div className="mb-3 flex items-start gap-2 text-[11px] text-indigo-900 bg-indigo-50 border border-indigo-200 rounded-lg p-2.5" data-testid="finance-result-preview">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-indigo-600" />
                  <p className="leading-snug"><b>Prévia (receita prevista).</b> Os valores de caixa e sócios só são oficializados quando o orçamento chega em <b>Nota paga</b>.</p>
                </div>
              )}
              {/* Header card */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-black">
                  Nota fiscal {data.nf_number ? `#${data.nf_number}` : "sem número"}
                </p>
                <p className="text-[13px] font-black text-slate-900 leading-tight mt-0.5 truncate">
                  Orçamento Nº {data.budget_number} — {data.school_name}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[11px] text-slate-600 font-semibold">Valor bruto</span>
                  <span className="text-xl font-black text-slate-900 tabular-nums">
                    {BRL(data.gross || 0)}
                  </span>
                </div>
              </div>

              {/* Deductions */}
              <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50/40 p-3">
                <p className="text-[10px] uppercase tracking-widest text-rose-700 font-black mb-1.5">
                  Deduções
                </p>
                <MinusRow icon={Receipt} label="ISS" hint="Retido na fonte — não chega até nós" value={data.iss_value} danger />
              </div>

              <div className="mt-3 rounded-xl p-3 bg-slate-50 border border-slate-200 flex items-center justify-between" data-testid="finance-result-received">
                <span className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Recebido (nota − ISS)</span>
                <span className="text-base font-black tabular-nums text-slate-900">{BRL(data.received_value ?? (data.gross || 0) - (data.iss_value || 0))}</span>
              </div>

              <div className="mt-3 rounded-xl p-3 bg-sky-50 border border-sky-200 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-sky-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] uppercase font-black tracking-widest text-sky-700">Caixa da empresa</p>
                  <p className="text-[10px] text-sky-800">{data.reserve_pct}% do recebido</p>
                </div>
                <p className="text-base font-black text-sky-800 tabular-nums" data-testid="finance-result-reserve">{BRL(data.reserve_value || 0)}</p>
              </div>

              <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50/40 p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] uppercase tracking-widest text-rose-700 font-black">Do restante ({BRL(data.after_reserve || 0)}) saem</p>
                </div>
                <MinusRow icon={Receipt} label="Simples Nacional" hint="Fica separado para pagar o DAS (dia 20)" value={data.simples_value} danger />
                {Object.keys(EXP_LABEL).map((k) => (
                  <MinusRow key={k} icon={TrendingDown} label={EXP_LABEL[k]} value={(data.expenses_breakdown || {})[k] || 0} danger={((data.expenses_breakdown || {})[k] || 0) > 0} />
                ))}
                <div className="mt-1 pt-2 border-t border-rose-200 flex items-center justify-between" data-testid="finance-result-expenses-total">
                  <span className="text-[11px] font-black text-rose-800">Simples + gastos (fica na conta do dia a dia)</span>
                  <span className="text-[13px] font-black tabular-nums text-rose-700">− {BRL(data.bills_value ?? (data.simples_value || 0) + (data.expenses_total || 0))}</span>
                </div>
              </div>
              {data.shortfall > 0 && (
                <p className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2" data-testid="finance-result-shortfall">
                  O restante não cobre Simples + gastos: faltam {BRL(data.shortfall)}. Sem distribuição aos sócios.
                  {data.month_pool != null && (
                    <span className="block mt-1 font-semibold" data-testid="finance-result-month-pool">
                      Somando as {data.month_notes} notas do mês, {data.month_pool >= 0 ? `sobram ${BRL(data.month_pool)} para a divisão.` : `ainda faltam ${BRL(-data.month_pool)} no mês.`}
                    </span>
                  )}
                </p>
              )}

              <p className="mt-2 text-[10.5px] text-slate-600" data-testid="finance-result-margin">Margem do serviço: <b>{Number(data.margin_pct || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%</b> • Lucro após impostos e gastos: <b data-testid="finance-result-net">{BRL(data.net_after_taxes_expenses || 0)}</b></p>

              {data.distributable > 0 && (
                <>
                  {/* Socios distribution */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] uppercase font-black tracking-widest text-emerald-700">
                        Distribuição aos sócios
                      </p>
                      <p className="text-[10px] text-slate-500">
                        Total: {BRL(data.distributable || 0)}
                      </p>
                    </div>
                    <ul className="space-y-1.5">
                      {(data.distributions || []).map((d) => (
                        <li
                          key={d.socio_id}
                          data-testid={`finance-socio-${d.socio_id}`}
                          className="flex items-center gap-2.5 bg-white border border-emerald-200 rounded-lg p-2.5"
                        >
                          <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 font-black text-xs">
                            <Users className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12.5px] font-black text-slate-900 leading-tight truncate">
                              {d.socio_name}
                            </p>
                            <p className="text-[10px] text-emerald-700 font-semibold">
                              {d.pct}% • faixa {d.tier}
                            </p>
                          </div>
                          <p className="text-sm font-black text-emerald-700 tabular-nums">
                            {BRL(d.value || 0)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              <AdjustmentsBox data={data} />

              <div className="mt-3 rounded-xl p-3 bg-slate-900 text-white" data-testid="finance-result-final">
                <p className="text-[10px] uppercase tracking-widest font-black text-white/70">Resultado financeiro do serviço</p>
                <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
                  <span className="text-white/70">Recebido</span><span className="text-right font-bold tabular-nums">{BRL(data.gross || 0)}</span>
                  <span className="text-white/70">Gasto (despesas)</span><span className="text-right font-bold tabular-nums">{BRL(data.expenses_total || 0)}</span>
                  <span className="text-white/70">Impostos</span><span className="text-right font-bold tabular-nums">{BRL((data.iss_value || 0) + (data.simples_value || 0))}</span>
                  <span className="text-white/70">Caixa da empresa</span><span className="text-right font-bold tabular-nums">{BRL(data.reserve_value || 0)}</span>
                  <span className="text-white/70">Sócios</span><span className="text-right font-bold tabular-nums">{BRL(data.distributable || 0)}</span>
                  <span className="font-black">Resultado líquido</span><span className={`text-right font-black tabular-nums ${data.net_after_taxes_expenses < 0 ? "text-rose-300" : "text-emerald-300"}`}>{BRL(data.net_after_taxes_expenses || 0)}</span>
                </div>
              </div>

              <div className="mt-3 flex items-start gap-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
                <p className="leading-snug">
                  {isPreview ? "Prévia atualizada a cada despesa lançada." : "Apuração salva. Qualquer alteração em despesas ou dados fiscais recalcula automaticamente, sem duplicar valores."}
                </p>
              </div>
            </>
          )}

          <Button
            onClick={close}
            className="w-full h-11 mt-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold text-sm"
            data-testid="finance-result-ok"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
